import Alpine from 'alpinejs';
import { requestCurrentUser } from '../../utils/currentUserRequest.js';

let pendingFetch = null;
let sessionGeneration = 0;

Alpine.store('user', {
    isFetched: false,
    fetchError: null,
    isLoggedIn: false,
    isAdmin: false,
    showDebugFeatures: false,
    homeownerDiscountCandidate: false,
    id: null,
    fullName: null,
    email: null,
    phoneNumber: null,
    primaryPhoneNumber: null, // Alias for form compatibility
    addressId: null,
    city: null,
    locality: null,
    state: null,
    stateFull: null,
    bio: null,
    bioPlain: null, // Plain text version for textarea
    photoUrl: null,
    yearCreated: null,
    selectedTravelPreferences: [],

    get canShowDebugFeatures() {
        return (
            this.isFetched &&
            this.isLoggedIn &&
            this.isAdmin &&
            this.showDebugFeatures
        );
    },

    get firstName() {
        if (!this.fullName) return '';
        const parts = this.fullName.trim().split(' ');
        return parts[0] || '';
    },

    get lastName() {
        if (!this.fullName) return '';
        const parts = this.fullName.trim().split(' ');
        return parts.slice(1).join(' ') || '';
    },

    // Computed property for formatted location
    get location() {
        if (this.city && this.stateFull) {
            return `${this.city}, ${this.stateFull}`;
        }
        if (this.city) return this.city;
        if (this.stateFull) return this.stateFull;
        return null;
    },

    async init() {
        await this.fetchUser();
    },

    clearUserState() {
        this.isAdmin = false;
        this.showDebugFeatures = false;
        this.homeownerDiscountCandidate = false;
        this.id = null;
        this.fullName = null;
        this.email = null;
        this.phoneNumber = null;
        this.primaryPhoneNumber = null;
        this.addressId = null;
        this.city = null;
        this.locality = null;
        this.state = null;
        this.stateFull = null;
        this.bio = null;
        this.bioPlain = null;
        this.photoUrl = null;
        this.yearCreated = null;
        this.selectedTravelPreferences = [];
    },

    clearDependentUserState() {
        // Mark cleared caches as loaded so logged-out UI does not refetch
        // protected wishlist endpoints with the expired session.
        const wishlistStore = Alpine.store('wishlist');
        if (wishlistStore) {
            wishlistStore.items = [];
            wishlistStore.isLoaded = true;
        }

        const wishlistModalStore = Alpine.store('wishlistModal');
        if (wishlistModalStore) {
            if (typeof wishlistModalStore.setWishlists === 'function') {
                wishlistModalStore.setWishlists([]);
            } else {
                wishlistModalStore.wishlists = [];
            }
            wishlistModalStore.isLoaded = true;
        }
    },

    markSessionExpired() {
        sessionGeneration++;
        pendingFetch = null;
        this.isLoggedIn = false;
        this.isFetched = true;
        this.clearUserState();
        this.clearDependentUserState();
    },

    whenReady() {
        return pendingFetch || Promise.resolve(this.isFetched);
    },

    async fetchUser({ fresh = false } = {}) {
        // A post-write assertion must start after any pre-write read finishes.
        if (fresh && pendingFetch) await pendingFetch;
        if (pendingFetch) return pendingFetch;

        const generation = sessionGeneration;
        const pending = this.loadUser(generation);
        pendingFetch = pending;
        try {
            return await pending;
        } finally {
            if (pendingFetch === pending) pendingFetch = null;
        }
    },

    async loadUser(generation) {
        this.fetchError = null;
        try {
            const data = await requestCurrentUser();
            if (generation !== sessionGeneration) return null;

            this.isLoggedIn = data.isLoggedIn === 'true';

            // Update wishlist store with user's wishlisted items
            const wishlistStore = Alpine.store('wishlist');
            if (wishlistStore) {
                wishlistStore.items = (data.wishlistedIds || []).map((id) =>
                    parseInt(id, 10)
                );
                wishlistStore.isLoaded = true;
            }

            // Update wishlist modal store with preloaded wishlists
            const wishlistModalStore = Alpine.store('wishlistModal');
            if (wishlistModalStore) {
                if (typeof wishlistModalStore.setWishlists === 'function') {
                    wishlistModalStore.setWishlists(data.wishlists || []);
                } else {
                    wishlistModalStore.wishlists = data.wishlists || [];
                }
                wishlistModalStore.isLoaded = true;
            }

            // Clear session data if not logged in
            if (!this.isLoggedIn) {
                this.clearUserState();
            } else {
                // Only populate data when logged in
                this.isAdmin = data.isAdmin === true;
                this.showDebugFeatures = data.showDebugFeatures === true;
                this.homeownerDiscountCandidate =
                    data.homeownerDiscountCandidate === true;
                this.id = data.id ?? null;
                this.fullName = data.fullName ?? null;
                this.email = data.email ?? null;
                this.phoneNumber = data.phoneNumber ?? null;
                this.primaryPhoneNumber = data.phoneNumber ?? null; // Alias
                this.addressId = data.addressId ?? null;
                this.city = data.city ?? null;
                this.locality = data.locality ?? null;
                this.state = data.state ?? null;
                this.stateFull = data.stateFull ?? null;
                this.bio = data.bio ?? null;
                // Strip HTML tags for plain text version
                this.bioPlain = data.bio
                    ? data.bio.replace(/<[^>]*>/g, '').trim()
                    : null;
                this.photoUrl = data.photoUrl ?? null;
                this.yearCreated = data.yearCreated ?? null;
                this.selectedTravelPreferences =
                    data.selectedTravelPreferences ?? [];
            }

            this.isFetched = true;
            return data;
        } catch (error) {
            console.error('Error fetching user data:', error);
            if (generation === sessionGeneration && error.status === 401) {
                this.markSessionExpired();
            } else if (generation === sessionGeneration) {
                // A transport failure cannot establish that the session expired.
                this.fetchError = error.message;
                this.isFetched = false;
            }
            return null;
        }
    }
});
